import moment from 'moment';
import {Dialog} from '@capacitor/dialog';
import {Geolocation, Position} from '@capacitor/geolocation';
import {Device} from '@capacitor/device';
import {Directory, Filesystem} from '@capacitor/filesystem';
import {FileOpener} from '@capacitor-community/file-opener';
import {ref, Ref, UnwrapRef, watch} from 'vue';

export type Nullable<T extends object> = {
  [K in keyof T]: T[K] | null;
}

export function autoImplement<T extends {}>(defaults: Partial<T> = {}) {
  return class {
    constructor(data: Partial<T> = {}) {
      Object.assign(this, defaults);
      Object.assign(this, data);
    }
  } as new (data?: T) => T;
}

export function momentFromDateAndTime(date: string, time: string | null) {
  let dateToParse = date;
  if (time) {
    dateToParse = `${date.substring(0, date.indexOf('T') + 1)}${time}`;
  }

  return moment(dateToParse);
}

export function deepClone<T>(object: T): T {
  switch (typeof object) {
    case 'object':
      if (object === null) return null as unknown as T;
      if (Array.isArray(object)) return object.map((i) => deepClone(i)) as unknown as T; // deep clone every element in array

      const collectedObject = {} as unknown as T;

      // eslint-disable-next-line guard-for-in
      for (const i in object) {
        collectedObject[i] = deepClone(object[i]);
      }

      return collectedObject;
    default:
      // Copied anyway
      return object;
  }
}

export type ArrayElement<ArrayType extends readonly unknown[]>
  = ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export function parseGermanFloat(val: number | string): number {
  if (typeof val === 'string') {
    val = val.replaceAll(',', '.');
    val = parseFloat(val);
  }

  if (Number.isNaN(val)) return 0;

  return val;
}

export function groupBy<T extends {}, K extends keyof T>(items: T[], key: K) {
  return items.reduce(
      (result, item) => ({
        ...result,
        [item[key] as unknown as string]: [
          ...(result[item[key] as unknown as string] || []),
          item,
        ],
      }),
    {} as { [key: string]: T[] },
  );
}

export function roundToOneOverX(num: number, x: number = 100, doRound: (num: number) => number = Math.round) {
  return doRound(num * x) / x;
}

export function makePercentage(n: number, of: number): number {
  return roundToOneOverX(n / of);
}

export async function doConfirm(message: string, title = 'Bitte bestätigen', {
  ok = 'OK',
  cancel = 'Abbrechen',
} = {}): Promise<boolean> {
  const result = await Dialog.confirm({
    title,
    message,
    okButtonTitle: ok,
    cancelButtonTitle: cancel,
  });

  return result.value;
}

export async function doAlert(message: string, title = '', buttonTitle = 'OK'): Promise<void> {
  await Dialog.alert({
    message,
    title,
    buttonTitle,
  });
}

export async function setRef<T>(ref: Ref<T>, val: T): Promise<void> {
  if (ref.value === val) return;
  let setPromRes = () => {
  };
  const setProm = new Promise<void>((res) => {
    setPromRes = res;
  });
  const stopWatching = watch(ref, (newVal: T) => {
    if (newVal !== val) return;
    setPromRes();
    stopWatching();
  });

  ref.value = val;

  return setProm;
}

export async function waitForChange<T>(ref: Ref<T>): Promise<void> {
  let changePromRes = () => {
  };
  const changeProm = new Promise<void>((res) => {
    changePromRes = res;
  });
  const stopWatching = watch(ref, () => {
    changePromRes();
    stopWatching();
  });

  return changeProm;
}

export async function waitForChangeTo<T>(ref: Ref<T>, val: T): Promise<void> {
  let changePromRes = () => {
  };
  const changeProm = new Promise<void>((res) => {
    changePromRes = res;
  });
  const stopWatching = watch(ref, (newVal: T) => {
    if (newVal !== val) return;
    changePromRes();
    stopWatching();
  });

  return changeProm;
}

export async function waitForValue<T>(ref: Ref<T>, val: T): Promise<void> {
  let changePromRes = () => {
  };
  const changeProm = new Promise<void>((res) => {
    changePromRes = res;
  });
  const stopWatching = watch(ref, (newVal: T) => {
    if (newVal !== val) return;
    changePromRes();
    stopWatching();
  });

  return changeProm;
}

export async function getCurrentPosition(): Promise<Position> {
  return await Geolocation.getCurrentPosition({
    enableHighAccuracy: false,
    maximumAge: 60_000, // 1 minute
  });
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => void resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

export async function openBlob(data: Blob, filename: string) {
  if ((await Device.getInfo()).platform === 'web') {
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    // the filename you want
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    return;
  }

  const file = await Filesystem.writeFile({
    path: filename,
    directory: Directory.Cache,
    // encode to base64; will only save raw base64 part
    data: await blobToBase64(data),
  });

  await FileOpener.open({
    filePath: file.uri,
  });
}

export function base64ToBlob(base64: string, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, {type: contentType});
  return blob;
}

export async function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

// eslint-disable-next-line valid-jsdoc
/**
 * writes return value of `getter` in ref
 */
export function asyncRef<T>(defaultValue: T, getter: () => Promise<UnwrapRef<T>>): Ref<UnwrapRef<T>> {
  const val = ref(defaultValue);

  (async () => {
    val.value = await getter();
  })();

  return val;
}

export function deepGet(data: any, ...path: Array<string | number>): unknown {
  if (!path.length) return data;
  let currentValue = data;
  do {
    currentValue = currentValue[path.unshift()];
    if (currentValue === undefined) return undefined;
  } while (path.length);

  return currentValue;
}


export function deepSet(data: any, newValue: any, ...path: Array<string | number>) {
  if (!path.length) return data;
  let currentValue = data;
  while (path.length > 1) {
    currentValue = currentValue[path.splice(0, 1)[0]];
    if (currentValue === undefined) throw new Error('could not deep set value, as not defined');
  }

  currentValue[path.splice(0, 1)[0]] = newValue;
}

export function deepRemove(data: any, ...path: Array<string | number>) {
  if (!path.length) return data;
  let currentValue = data;
  while (path.length > 1) {
    currentValue = currentValue[path.splice(0, 1)[0]];
    if (currentValue === undefined) throw new Error('could not deep remove value, as not defined');
  }

  if (Array.isArray(currentValue)) {
    currentValue.splice(path.splice(0, 1)[0] as number);
  } else {
    delete currentValue[path.splice(0, 1)[0]];
  }
}

export function humanFileSize(bytes: number, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si ?
    ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] :
    ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


  return bytes.toFixed(dp) + ' ' + units[u];
}
